import { useState, useEffect } from 'react';
import {
  Settings,
  Save,
  Loader2,
  Trophy,
  Zap,
  Target,
  Award,
  Plus,
  Trash2
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { getAuthHeaders } from '../../services/adminApi';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Japanese mastery level labels
const MASTERY_LEVELS = [
  { key: 'shokyu', label: 'Shokyu (初級)', english: 'Beginner', color: 'bg-green-500' },
  { key: 'chukyu', label: 'Chukyu (中級)', english: 'Intermediate', color: 'bg-blue-500' },
  { key: 'jokyu', label: 'Jokyu (上級)', english: 'Advanced', color: 'bg-purple-500' },
  { key: 'tatsujin', label: 'Tatsujin (達人)', english: 'Master', color: 'bg-yellow-500' },
];

const DIFFICULTY_LEVELS = [1, 2, 3, 4, 5];

/**
 * VocabPointsSettings - Admin modal for configuring vocabulary point values
 *
 * Features:
 * - Base point values (incorrect, correct learning, correct mastered)
 * - Per-level mastery thresholds and bonuses (Shokyu/Chukyu/Jokyu/Tatsujin)
 * - Category completion bonuses
 * - Difficulty multipliers
 * - Correct-answer streak milestones
 */
export default function VocabPointsSettings({ open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Config state
  const [config, setConfig] = useState({
    vocab_incorrect: 5,
    vocab_correct_learning: 10,
    vocab_correct_mastered: 5,
    mastery_levels: [
      { level: 'shokyu', threshold: 2, bonus_points: 5 },
      { level: 'chukyu', threshold: 4, bonus_points: 10 },
      { level: 'jokyu', threshold: 8, bonus_points: 25 },
      { level: 'tatsujin', threshold: 16, bonus_points: 50 },
    ],
    category_level_bonuses: [
      { level: 'shokyu', bonus_points: 25 },
      { level: 'chukyu', bonus_points: 50 },
      { level: 'jokyu', bonus_points: 100 },
      { level: 'tatsujin', bonus_points: 200 },
    ],
    difficulty_multipliers: { 1: 1.0, 2: 1.0, 3: 1.0, 4: 1.0, 5: 1.0 },
    correct_streak_milestones: [
      { milestone: 5, bonus_points: 15 },
      { milestone: 10, bonus_points: 30 },
      { milestone: 25, bonus_points: 75 },
      { milestone: 50, bonus_points: 150 },
    ],
  });

  // Fetch config on mount
  useEffect(() => {
    if (open) {
      fetchConfig();
    }
  }, [open]);

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/admin/vocab-points-config`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setConfig({
          vocab_incorrect: data.vocab_incorrect ?? 5,
          vocab_correct_learning: data.vocab_correct_learning ?? 10,
          vocab_correct_mastered: data.vocab_correct_mastered ?? 5,
          mastery_levels: data.mastery_levels || config.mastery_levels,
          category_level_bonuses: data.category_level_bonuses || config.category_level_bonuses,
          difficulty_multipliers: data.difficulty_multipliers || config.difficulty_multipliers,
          correct_streak_milestones: data.correct_streak_milestones || config.correct_streak_milestones,
        });
      }
    } catch (err) {
      console.error('Failed to fetch vocab points config:', err);
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/admin/vocab-points-config`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });
      if (response.ok) {
        onClose();
      } else {
        throw new Error('Failed to save configuration');
      }
    } catch (err) {
      console.error('Failed to save vocab points config:', err);
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Update mastery level threshold or bonus
  const updateMasteryLevel = (levelKey, field, value) => {
    setConfig(prev => ({
      ...prev,
      mastery_levels: prev.mastery_levels.map(ml =>
        ml.level === levelKey ? { ...ml, [field]: parseInt(value) || 0 } : ml
      )
    }));
  };

  // Update category bonus
  const updateCategoryBonus = (levelKey, value) => {
    setConfig(prev => ({
      ...prev,
      category_level_bonuses: prev.category_level_bonuses.map(clb =>
        clb.level === levelKey ? { ...clb, bonus_points: parseInt(value) || 0 } : clb
      )
    }));
  };

  // Update difficulty multiplier
  const updateMultiplier = (difficulty, value) => {
    setConfig(prev => ({
      ...prev,
      difficulty_multipliers: {
        ...prev.difficulty_multipliers,
        [difficulty]: parseFloat(value) || 1.0
      }
    }));
  };

  // Update streak milestone
  const updateStreakMilestone = (index, field, value) => {
    setConfig(prev => ({
      ...prev,
      correct_streak_milestones: prev.correct_streak_milestones.map((ms, i) =>
        i === index ? { ...ms, [field]: parseInt(value) || 0 } : ms
      )
    }));
  };

  // Add streak milestone
  const addStreakMilestone = () => {
    setConfig(prev => ({
      ...prev,
      correct_streak_milestones: [
        ...prev.correct_streak_milestones,
        { milestone: 100, bonus_points: 200 }
      ]
    }));
  };

  // Remove streak milestone
  const removeStreakMilestone = (index) => {
    setConfig(prev => ({
      ...prev,
      correct_streak_milestones: prev.correct_streak_milestones.filter((_, i) => i !== index)
    }));
  };

  const getMasteryLevelData = (levelKey) => {
    return config.mastery_levels.find(ml => ml.level === levelKey) || { threshold: 0, bonus_points: 0 };
  };

  const getCategoryBonusData = (levelKey) => {
    return config.category_level_bonuses.find(clb => clb.level === levelKey) || { bonus_points: 0 };
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-purple-600" />
            Vocabulary Points Configuration
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
          </div>
        ) : (
          <div className="space-y-6">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Base Point Values */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Zap className="h-4 w-4 text-yellow-500" />
                  Base Point Values
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Incorrect Answer</Label>
                  <Input
                    type="number"
                    min="0"
                    value={config.vocab_incorrect}
                    onChange={(e) => setConfig(prev => ({ ...prev, vocab_incorrect: parseInt(e.target.value) || 0 }))}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Points for wrong answers</p>
                </div>
                <div>
                  <Label>Correct (Learning)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={config.vocab_correct_learning}
                    onChange={(e) => setConfig(prev => ({ ...prev, vocab_correct_learning: parseInt(e.target.value) || 0 }))}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Not yet mastered words</p>
                </div>
                <div>
                  <Label>Correct (Mastered)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={config.vocab_correct_mastered}
                    onChange={(e) => setConfig(prev => ({ ...prev, vocab_correct_mastered: parseInt(e.target.value) || 0 }))}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Already mastered words</p>
                </div>
              </CardContent>
            </Card>

            {/* Mastery Levels */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-purple-500" />
                  Mastery Levels
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Configure the number of correct answers required to reach each mastery level and the bonus points awarded.
                </p>
                <div className="space-y-3">
                  {MASTERY_LEVELS.map(({ key, label, english, color }) => {
                    const levelData = getMasteryLevelData(key);
                    return (
                      <div key={key} className="flex items-center gap-4">
                        <div className="w-40 flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${color}`} />
                          <div>
                            <div className="font-medium text-sm">{label}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{english}</div>
                          </div>
                        </div>
                        <div className="flex-1 grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Threshold (correct answers)</Label>
                            <Input
                              type="number"
                              min="1"
                              value={levelData.threshold}
                              onChange={(e) => updateMasteryLevel(key, 'threshold', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Bonus Points</Label>
                            <Input
                              type="number"
                              min="0"
                              value={levelData.bonus_points}
                              onChange={(e) => updateMasteryLevel(key, 'bonus_points', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Category Completion Bonuses */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Award className="h-4 w-4 text-blue-500" />
                  Category Completion Bonuses
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Bonus points when ALL words in a category/difficulty reach each mastery level.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {MASTERY_LEVELS.map(({ key, label, color }) => {
                    const bonusData = getCategoryBonusData(key);
                    return (
                      <div key={key}>
                        <Label className="text-xs flex items-center gap-1">
                          <div className={`w-2 h-2 rounded-full ${color}`} />
                          {label.split(' ')[0]}
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          value={bonusData.bonus_points}
                          onChange={(e) => updateCategoryBonus(key, e.target.value)}
                        />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Difficulty Multipliers */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Target className="h-4 w-4 text-red-500" />
                  Difficulty Multipliers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Multiply points based on word difficulty level. Default is 1.0 (no change).
                </p>
                <div className="grid grid-cols-5 gap-3">
                  {DIFFICULTY_LEVELS.map(level => (
                    <div key={level}>
                      <Label className="text-xs">Level {level}</Label>
                      <Input
                        type="number"
                        min="0.1"
                        max="10"
                        step="0.1"
                        value={config.difficulty_multipliers[level] ?? 1.0}
                        onChange={(e) => updateMultiplier(level, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Correct-Answer Streak Bonuses */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 justify-between">
                  <span className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-orange-500" />
                    Correct-Answer Streak Bonuses
                  </span>
                  <Button variant="outline" size="sm" onClick={addStreakMilestone}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Bonus points for consecutive correct answers in a session.
                </p>
                <div className="space-y-2">
                  {config.correct_streak_milestones.map((ms, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="flex-1">
                        <Label className="text-xs">Streak Count</Label>
                        <Input
                          type="number"
                          min="1"
                          value={ms.milestone}
                          onChange={(e) => updateStreakMilestone(index, 'milestone', e.target.value)}
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs">Bonus Points</Label>
                        <Input
                          type="number"
                          min="0"
                          value={ms.bonus_points}
                          onChange={(e) => updateStreakMilestone(index, 'bonus_points', e.target.value)}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-5 text-red-500 hover:text-red-700"
                        onClick={() => removeStreakMilestone(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading || saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Configuration
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
